/**
 * Responsibility: choose the order the collection is shown in.
 *
 * A button rather than a dropdown sitting open in the toolbar: the toolbar
 * already carries the view switch, the filter button and edit mode, and a
 * fourth always-visible control crowds the row on a phone. The current order
 * is written on the button, so it never has to be opened to be read.
 */
import { useState } from 'react';
import type { MouseEvent } from 'react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import SortIcon from '@mui/icons-material/Sort';

import { SORT_OPTIONS } from './collectionSort';
import type { CollectionSort } from './collectionSort';

type Props = {
  value: CollectionSort;
  onChange: (value: CollectionSort) => void;
  disabled?: boolean;
};

export function CollectionSortMenu({ value, onChange, disabled = false }: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const current = SORT_OPTIONS.find((option) => option.value === value) ?? SORT_OPTIONS[0];

  const open = (event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget);
  const close = () => setAnchor(null);

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<SortIcon />}
        onClick={open}
        disabled={disabled}
        aria-haspopup="menu"
      >
        {current.label}
      </Button>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={close}>
        {SORT_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => {
              onChange(option.value);
              close();
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
